import type { CrawlLane, CrawlTrigger, TaskType } from "./types";
import { isFreeLane } from "./lanes";

/** Max items targeted per depth scrape page (v2 often returns up to 100). */
export const CRAWL_PAGE_SIZE = 100;

/** Target list depth per side at steady state. */
export const LIST_DEPTH_TARGET = 1000;

/** Steady-state pages per followers/following job. */
export const LIST_PAGES_STEADY = Math.ceil(LIST_DEPTH_TARGET / CRAWL_PAGE_SIZE);

/** Worker job pages on initial connect (executor may call v2 multiple times inside page 1). */
export const INITIAL_LIST_PAGES = 1;

/** Minimum followers/following to collect on initial connect. */
export const INITIAL_LIST_MIN_COUNT = 50;

/** If a single v2 page returns this many, stop paginating on initial. */
export const INITIAL_LIST_SINGLE_PAGE_OK = 50;

/** Safety cap on v2 API calls inside one initial list page. */
export const INITIAL_LIST_MAX_API_CALLS = 5;

/** Top N relationships marked RECENT after initial seed (legacy cap - prefer initialRecentCountForTotal). */
export const INITIAL_RECENT_COUNT = 5;

/** Floor for initial RECENT markers per side. */
export const INITIAL_RECENT_MIN = 2;

/** Ceiling for initial RECENT markers per side. */
export const INITIAL_RECENT_MAX = 22;

/**
 * How many top list rows to mark RECENT on initial connect for one side.
 * Scales with the account's public total so small accounts show fewer than large ones.
 */
export function initialRecentCountForTotal(
  total: number,
  collectedCount: number
): number {
  if (collectedCount <= 0) return 0;

  const safeTotal = Math.max(0, total);
  let n: number;

  if (safeTotal < 75) n = 2;
  else if (safeTotal < 250) n = 3;
  else if (safeTotal < 1_000) n = 5;
  else if (safeTotal < 5_000) n = 7;
  else if (safeTotal < 15_000) n = 10;
  else if (safeTotal < 50_000) n = 14;
  else if (safeTotal < 200_000) n = 18;
  else n = INITIAL_RECENT_MAX;

  return Math.max(
    INITIAL_RECENT_MIN,
    Math.min(n, INITIAL_RECENT_MAX, collectedCount)
  );
}

/** Rolling posts window (days) - filter by each post's IG postedAt. */
export const POSTS_LOOKBACK_DAYS = 7;

/** Max posts collected per scheduled scrape (7-day window). */
export const POSTS_MAX_COUNT = 25;

/** Initial connect fallback when 7-day window is empty. */
export const POSTS_INITIAL_FALLBACK_COUNT = 5;

/** Safety cap on post pagination pages per job. */
export const POSTS_MAX_PAGES = 5;

/** Min post age before likes scrape (ms). */
export const ENGAGEMENT_MIN_AGE_MS = 24 * 60 * 60 * 1000;

/** MVP ingest task types (stories deferred). */
export const MVP_TASK_TYPES: TaskType[] = ["followers", "following", "posts"];

/** @deprecated Use MVP_TASK_TYPES */
export const PHASE1_TASK_TYPES: TaskType[] = MVP_TASK_TYPES;

/** Max rows shown in dashboard follower/following tables (matches list page size). */
export const FOLLOW_LIST_DISPLAY_LIMIT = CRAWL_PAGE_SIZE;

/** Items per page for free-tier jobs (legacy - no free crawl in MVP). */
export const FREE_PAGE_SIZE = 10;

/** Lane priority scores - higher = leased first. */
export const LANE_PRIORITY: Record<CrawlLane, number> = {
  p0_initial: 1000,
  p1_hourly: 800,
  p2_daily: 600,
  p3_free: 400,
  p4_weekly: 200,
};

/** Global API rate budget (requests per minute). */
export const RATE_LIMIT_TOTAL = Number(process.env.CRAWL_RATE_LIMIT_TOTAL ?? 50);

/** Paid-lane pool within the minute window. */
export const RATE_LIMIT_PAID = Number(process.env.CRAWL_RATE_LIMIT_PAID ?? 40);

/** Free-lane reserved pool within the minute window. */
export const RATE_LIMIT_FREE = Number(process.env.CRAWL_RATE_LIMIT_FREE ?? 10);

/** How long a worker lease is valid before another worker can reclaim. */
export const LEASE_DURATION_MS = Number(process.env.CRAWL_LEASE_MS ?? 10 * 60 * 1000);

/** Max pagination retries per page. */
export const MAX_PAGE_ATTEMPTS = 3;

/** Max job-level attempts (re-queue whole job). */
export const MAX_JOB_ATTEMPTS = 3;

/** Scheduling timezone for MVP. */
export const SCHEDULE_TZ = "America/Chicago";

/** Page size for a given lane. */
export function pageSizeForLane(lane: CrawlLane): number {
  return isFreeLane(lane) ? FREE_PAGE_SIZE : CRAWL_PAGE_SIZE;
}

/** Lanes the worker leases (paid MVP). */
export const WORKER_LEASE_LANES: CrawlLane[] = [
  "p0_initial",
  "p2_daily",
  "p4_weekly",
];

export interface MaxPagesJobInput {
  taskType: TaskType;
  trigger: CrawlTrigger;
  maxPages?: number | null;
}

/** Per-job page limit - replaces global PAID_MAX_PAGES_PER_JOB. */
export function maxPagesForJob(job: MaxPagesJobInput): number {
  if (job.maxPages != null && job.maxPages > 0) {
    return job.maxPages;
  }

  if (job.taskType === "followers" || job.taskType === "following") {
    if (job.trigger === "initial") return INITIAL_LIST_PAGES;
    if (job.trigger === "depth_expansion" || job.trigger === "weekly_report") {
      return LIST_PAGES_STEADY;
    }
    return LIST_PAGES_STEADY;
  }

  if (job.taskType === "posts") return POSTS_MAX_PAGES;
  if (job.taskType === "likes") return 1;
  if (job.taskType === "stories") return 1;

  return 1;
}

/** @deprecated Use maxPagesForJob */
export function maxPagesForLane(lane: CrawlLane): number {
  return isFreeLane(lane) ? 1 : INITIAL_LIST_PAGES;
}

/** Plan → scrape interval for scheduled dispatcher (ms). */
export const PLAN_INTERVAL_MS: Record<
  import("./types").PlanTier,
  Record<TaskType, number | null>
> = {
  free: {
    followers: null,
    following: null,
    stories: null,
    posts: null,
    reels: null,
    highlights: null,
    likes: null,
  },
  pro: {
    followers: null,
    following: null,
    stories: null,
    posts: 24 * 60 * 60 * 1000,
    reels: null,
    highlights: null,
    likes: null,
  },
  agency: {
    followers: null,
    following: null,
    stories: null,
    posts: 24 * 60 * 60 * 1000,
    reels: null,
    highlights: null,
    likes: null,
  },
};
